import {
	ActionButton,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	SectionCard,
} from '@practium/ui';
import { UsersPageSkeleton } from '../components/Skeletons/UsersPageSkeleton';
import type { SectionTab } from '@practium/ui';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { BindingFarmersTable } from '../components/Tables/BindingFarmersTable/BindingFarmersTable';
import { BindingConsultantsTable } from '../components/Tables/BindingConsultantsTable/BindingConsultantsTable';
import type { UseBindingUsersPageProps } from '../hooks/useBindingUsersPage';
import { useBindingUsersPage } from '../hooks/useBindingUsersPage';
import type { BindingTab } from '../types';

export interface BindingUsersPageProps extends UseBindingUsersPageProps {
	onAddNew?: (tab: BindingTab) => void;
	onViewItem?: (tab: BindingTab, id: string) => void;
	onEnterProfile?: (tab: BindingTab, id: string) => void;
	onReportClick?: () => void;
}

export const BindingUsersPage = ({
	onAddNew,
	onViewItem,
	onEnterProfile,
	onReportClick,
	...hookProps
}: BindingUsersPageProps) => {
	const {
		activeTab,
		setActiveTab,
		filteredFarmers,
		filteredConsultants,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading,
		permissions,
		profilesPieData,
		insertionsLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		insertionsFooterText,
	} = useBindingUsersPage(hookProps);

	if (isLoading) return <UsersPageSkeleton tabsCount={2} />;

	const tabs: SectionTab<BindingTab>[] = [];

	if (permissions.canManageFarmers) {
		tabs.push({
			key: 'farmers',
			label: 'Agricultores',
			content: (
				<BindingFarmersTable
					farmers={filteredFarmers}
					onRowClick={onViewItem ? (id) => onViewItem('farmers', id) : undefined}
					onEnterProfile={
						permissions.canEnterProfile && onEnterProfile
							? (id) => onEnterProfile('farmers', id)
							: undefined
					}
				/>
			),
		});
	}

	if (permissions.canManageConsultants) {
		tabs.push({
			key: 'consultants',
			label: 'Consultores',
			content: (
				<BindingConsultantsTable
					consultants={filteredConsultants}
					onRowClick={onViewItem ? (id) => onViewItem('consultants', id) : undefined}
					onEnterProfile={
						permissions.canEnterProfile && onEnterProfile
							? (id) => onEnterProfile('consultants', id)
							: undefined
					}
				/>
			),
		});
	}

	const handleReport = () => onReportClick?.();

	return (
		<ListPageLayout
			sidebarWidth="28rem"
			sidebarW1500="22rem"
			sidebarW1300="19rem"
			sidebarW1200="17rem"
			sidebar={
				<ListPageCharts>
					<ListPageChartCard>
						<PiePercentageChart
							title="Distribuição de Perfis"
							subtitle="Distribuição de pessoas na plataforma"
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={profilesPieData}
						/>
					</ListPageChartCard>
					<ListPageChartCard>
						<ComparisonLineChart
							title="Inserções na Plataforma"
							mainValue={chartMainValue}
							trendValue={chartTrendValue}
							trendLabel="vs semana anterior"
							trendDirection={chartTrendDirection}
							footerText={insertionsFooterText}
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={insertionsLineData}
							xAxisKey="day"
							line1Key="atual"
							line1Label="Semana atual"
							line2Key="anterior"
							line2Label="Semana anterior"
							tooltipTitle="Inserções"
						/>
					</ListPageChartCard>
				</ListPageCharts>
			}
		>
			<SectionCard
				title="Gestão de Pessoas"
				subtitle="Gerencie agricultores e consultores vinculados à sua conta"
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				search={true}
				searchQuery={searchQuery}
				onSearch={setSearchQuery}
				sortAsc={sortAsc}
				onToggleSort={toggleSort}
				filterSlot={
					<FilterChip
						values={filterValues}
						onChange={setFilterValues}
						groups={filterGroups}
						placeholder="Filtros"
					/>
				}
				footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				headerAction={
					<>
						{permissions.canCreate && (
							<ActionButton text="Adicionar" onClick={() => onAddNew?.(activeTab)} />
						)}
						<ActionButton text="Ver Relatório" onClick={handleReport} />
					</>
				}
			/>
		</ListPageLayout>
	);
};